import React from 'react';
import css from './Template3Minimal.module.css';
import EditableRichField from '../EditableRichField';

const EditableField = props => <EditableRichField {...props} css={css} />;

const DeleteButton = ({ onClick, title = 'Delete Section' }) => (
  <button className={css.deleteSectionBtn} onClick={onClick} title={title}>
    ×
  </button>
);

const EditWrapper = ({ children, isEditMode, onDelete, className }) => {
  if (!isEditMode) return children;
  return (
    <div className={`${css.editWrapper} ${className || ''}`}>
      {onDelete && <DeleteButton onClick={onDelete} />}
      {children}
    </div>
  );
};

const EditableButton = ({ title, url, onTitleChange, onUrlChange, isEditMode, className, placeholderTitle, placeholderUrl }) => {
  if (isEditMode) {
    return (
      <div className={css.buttonEditContainer}>
        <input
          className={css.editableInput}
          type="text"
          value={title || ''}
          onChange={e => onTitleChange(e.target.value)}
          placeholder={placeholderTitle || 'Button Title'}
        />
        <input
          className={css.editableInput}
          type="text"
          value={url || ''}
          onChange={e => onUrlChange(e.target.value)}
          placeholder={placeholderUrl || 'Button URL'}
        />
      </div>
    );
  } else {
    return (
      <a className={className} href={url} target="_blank" rel="noopener noreferrer">
        {title}
      </a>
    );
  }
};

const Template3Minimal = ({ data, onChange, isEditMode, triggerConfirm }) => {
  const handleUpdate = (key, value) => {
    onChange(key, value);
  };

  const handleUpdateRow = (rowIndex, field, value) => {
    const newRows = [...(data?.rows || [])];
    newRows[rowIndex] = { ...newRows[rowIndex], [field]: value };
    onChange('rows', newRows);
  };

  const addRow = () => {
    const newRows = [...(data?.rows || [])];
    newRows.push({
      num: newRows.length + 1,
      title: 'Point or Section Title',
      text: 'Body copy goes here. The numbered circle system gives structure without feeling rigid.'
    });
    onChange('rows', newRows);
  };

  const deleteRow = (index) => {
    const newRows = (data?.rows || []).filter((_, i) => i !== index);
    onChange('rows', newRows);
  };

  return (
    <article className={css.t3}>
      <div className={css.t3Hero}>
        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t3Eyebrow}>
            <div className={css.t3LogoDot}>
              <span>F</span>
            </div>
            <EditableField
              tag="span"
              className={css.t3EyebrowText}
              value={data?.badge}
              onChange={val => handleUpdate('badge', val)}
              isEditMode={isEditMode}
              placeholder="Forteh — Blog Category"
            />
          </div>
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="h1"
            className={css.t3Title}
            value={data?.title}
            onChange={val => handleUpdate('title', val)}
            isEditMode={isEditMode}
            multiline
            placeholder="A Clean, Elegant Headline for Thoughtful Readers"
          />
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="p"
            className={css.t3Subtitle}
            value={data?.subtitle}
            onChange={val => handleUpdate('subtitle', val)}
            isEditMode={isEditMode}
            multiline
            placeholder="A single sentence subtitle that gives context and draws the reader in without giving too much away."
          />
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t3MetaRow}>
            <EditableField
              tag="span"
              className={css.t3Pill}
              value={data?.readTime}
              onChange={val => handleUpdate('readTime', val)}
              isEditMode={isEditMode}
              placeholder="4 min read"
            />
            <EditableField
              tag="span"
              className={css.t3Pill}
              value={data?.topicTag}
              onChange={val => handleUpdate('topicTag', val)}
              isEditMode={isEditMode}
              placeholder="Topic Tag"
            />
          </div>
        </EditWrapper>
      </div>

      <div className={css.t3Body}>
        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="p"
            className={css.t3Intro}
            value={data?.intro}
            onChange={val => handleUpdate('intro', val)}
            isEditMode={isEditMode}
            multiline
            placeholder="Your opening paragraph here — this template suits thoughtful, long-form content. Use the italic accent for your key insight, and let the clean layout do the heavy lifting visually."
          />
        </EditWrapper>

        {(data?.rows || []).map((row, index) => (
          <EditWrapper 
            key={index} 
            isEditMode={isEditMode} 
            onDelete={() => triggerConfirm('Delete Row?', `Remove row ${row?.num}?`, () => deleteRow(index))}
          >
            <div className={css.t3Row}>
              <div className={css.t3NumBlock}>
                <span>{row?.num}</span>
              </div>
              <div>
                <EditableField
                  tag="div"
                  className={css.t3RowTitle}
                  value={row?.title}
                  onChange={val => handleUpdateRow(index, 'title', val)}
                  isEditMode={isEditMode}
                  placeholder="Point or Section Title"
                />
                <EditableField
                  tag="p"
                  className={css.t3RowText}
                  value={row?.text}
                  onChange={val => handleUpdateRow(index, 'text', val)}
                  isEditMode={isEditMode}
                  multiline
                  placeholder="Body copy goes here. The numbered circle system gives structure without feeling rigid."
                />
              </div>
            </div>
          </EditWrapper>
        ))}

        {isEditMode && (
          <button className={css.addSectionBtn} onClick={addRow}>
            + Add Row
          </button>
        )}

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t3Quote}>
            <EditableField
              tag="p"
              value={data?.quote}
              onChange={val => handleUpdate('quote', val)}
              isEditMode={isEditMode}
              multiline
              placeholder="A quieter pull quote style — elegant and understated. Works well for credibility-focused or thought leadership posts."
            />
          </div>
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t3Cta}>
            <div>
              <EditableField
                tag="h3"
                value={data?.ctaTitle}
                onChange={val => handleUpdate('ctaTitle', val)}
                isEditMode={isEditMode}
                placeholder="Ready to take the next step?"
              />
              <EditableField
                tag="p"
                value={data?.ctaSubtext}
                onChange={val => handleUpdate('ctaSubtext', val)}
                isEditMode={isEditMode}
                placeholder="Browse talent or explore more insights at forteh.ca"
              />
            </div>
            <EditableButton
              title={data?.ctaButton}
              url={data?.ctaButtonUrl}
              onTitleChange={val => handleUpdate('ctaButton', val)}
              onUrlChange={val => handleUpdate('ctaButtonUrl', val)}
              isEditMode={isEditMode}
              className={css.t3Btn}
              placeholderTitle="Browse Talent →"
              placeholderUrl="https://forteh.ca"
            />
          </div>
        </EditWrapper>
      </div>
    </article>
  );
};

export default Template3Minimal;
